import React, { useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { TelerikReportViewer } from '@progress/telerik-react-report-viewer'
import { Box, Paper } from '@mui/material';

import { ThemeModeContext, apiRoute } from '../../App';
import { UserContext } from '../../context/UserContext';
import { formattedDate, formatTime } from '../../Utils';

import ReportPicker from './ReportPicker';

const ReportViewer = (props) => {

    // getting URLSearchParams for generating the specified report on page load
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const encodedReportData = queryParams.get("reportData");

    let reportData = {};
    if (encodedReportData) {
        reportData = JSON.parse(decodeURIComponent(encodedReportData))
    }


    const [reportsCategory, setReportsCategory] = useState("collection");
    const [mainParameters, setMainParameters] = useState('dateRange');
    const [reportName, setReportName] = useState(reportData.reportName);
    const reportParams = reportData.params;
    const currentDate = new Date();
    const formattedDefaultDate = currentDate.toISOString().split('T')[0];
    const [startDate, setStartDate] = useState(formattedDefaultDate);
    const [endDate, setEndDate] = useState(formattedDefaultDate);
    const [disburseDate, setDisburseDate] = useState();
    const [disburseId, setDisburseId] = useState();
    const [loadingDates, setLoadingDates] = useState(false);
    const [disburseDatesList, setDisburseDatesList] = useState([]);
    const [previousDate, setPreviousDate] = useState();

    let viewer;
    const { mode } = useContext(ThemeModeContext);
    const { entityId } = useContext(UserContext);

    const textFieldStyle = {
        '& .MuiInputBase-root': {
            backgroundColor: mode === "dark" ? "#4C4E52" : "white",
            color: mode === "dark" ? "white" : "black"
        },
        '& .MuiInputLabel-root': {
            color: mode === "dark" ? "white" : "black",
            textShadow: mode === 'dark' ? '1px 1px 2px black' : 'none',
        },
    }


    const reportOptions = [
        { key: 'BatchCollectionByDateRange', name: 'Batch Collection', category: 'collection', mainParameters: 'dateRange' },
        { key: 'BondForfeituresAndFeesCollection', name: 'Bond Forfeitures and Fees Collection', category: 'disbursement', mainParameters: 'entity' },
        { key: 'CasePayment', name: 'Case Payment', category: 'collection', mainParameters: 'dateRange' },
        { key: 'CashBondTransactions', name: 'Cash Bond Transactions', category: 'collection', mainParameters: 'dateRange' },
        { key: 'CashBondsBalance', name: 'Cash Bonds Balance', category: 'collection', mainParameters: 'entity' },
        { key: 'CashierCollections', name: 'Cashier Collections', category: 'collection', mainParameters: 'dateRange' },
        { key: 'CheckRegister', name: 'Check Register', category: 'disbursement', mainParameters: 'dateRange' },
        { key: 'Check&MoneyOrderCollections', name: 'Check and Money Order Collections', category: 'collection', mainParameters: 'dateRange' },
        { key: 'CMISFormA', name: 'CMIS Form A', category: 'disbursement', mainParameters: 'entity' },
        { key: 'CourtCostForNLCLC', name: 'Court Cost Collected for NLCLC', category: 'disbursement', mainParameters: 'entity' },
        { key: 'CreditCardPayment', name: 'Credit Card Payment', category: 'collection', mainParameters: 'dateRange' },
        { key: 'CreditCardChargeBack', name: 'Credit Card ChargeBack', category: 'collection', mainParameters: 'dateRange' },
        { key: 'DisbursementDetail', name: 'Disbursement Detail', category: 'disbursement', mainParameters: 'disbursement' },
        { key: 'DisbursementRecon', name: 'Disbursement Reconciliation', category: 'disbursement', mainParameters: 'disbursement' },
        { key: 'DistributionReport', name: 'Distribution Report', category: 'disbursement', mainParameters: 'disbursement' },
        { key: 'GLPostingJournal', name: 'GL Posting Journal', category: 'other', mainParameters: 'dateRange' },
        { key: 'LCLE_9903_9907_9919', name: 'LCLE 9903/9907/9919', category: 'disbursement', mainParameters: 'entity' },
        { key: 'LouisianaJudicialCollegeFormARevised', name: 'Louisiana Judicial College Form A', category: 'disbursement', mainParameters: 'entity' },
        { key: 'PaidCaseReport', name: 'Paid Case Report', category: "collection", mainParameters: 'dateRange' },
        { key: 'PaymentRegister', name: 'Payment Register', category: 'collection', mainParameters: 'dateRange' },
        { key: 'RefundReport', name: 'Refund Report', category: 'disbursement', mainParameters: 'dateRange' },
        { key: 'TrafficArraignment', name: 'Traffic Arraignment', category: 'other', mainParameters: 'entity' },
        { key: 'TypeBasedPaymentReport', name: 'Type Based Payment Report', category: 'collection', mainParameters: 'dateRange' }
    ]

    const switchReports = () => {

        if (mainParameters === 'dateRange') {
            const endDateParam = new Date(endDate);
            endDateParam.setDate(endDateParam.getDate() + 1)  
            endDateParam.setHours(23, 59, 59);
            const params = {
                startDate: new Date(startDate),
                endDate: endDateParam,
                EntityId: parseInt(entityId)
            }
            viewer?.setReportSource({ report: `${reportName}.trdp`, parameters: params });

        } else if (mainParameters === 'disbursement') {
            const params = { disburseId: disburseId, disburseDate: new Date(disburseDate), prevDate: new Date(previousDate), EntityId: parseInt(entityId) }
            viewer?.setReportSource({ report: `${reportName}.trdp`, parameters: params });

        } else if (mainParameters === 'entity') {
            const params = { EntityId: parseInt(entityId) }
            viewer?.setReportSource({ report: `${reportName}.trdp`, parameters: params });

        }
    }

    const getDisburseDates = () => {
        setLoadingDates(true);
        axios.get(`${apiRoute}/api/DisburseReportViews`)
            .then((res) => {
                const dateRows = res.data.map((row, i) => {
                    return {
                        id: `${row.disburseDate}-${i}`,
                        Date: formattedDate(row.disburseDate),
                        Time: formatTime(row.disburseDate),
                        FullDate: row.disburseDate,
                        EnteredBy: row.enteredBy,
                        Value: row.masterDisburseId
                    }
                })
                const sortedDateRows = dateRows.sort((a, b) => b.Value - a.Value)

                setDisburseDatesList(sortedDateRows);
            })
            .catch((err) => {
                console.error("error getting disburse dates", err);
            })
            .finally(() => setLoadingDates(false))
    };

    useEffect(() => {
        getDisburseDates();
    }, [])

    return (
        <Box sx={{ display: 'flex', height: '90vh', gap: 2, mt: '.5vh' }} >
            {/* Report Picker and configuration section */}
            <Paper
                elevation={10}
                sx={{
                    flex: '0 0 35%',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Paper className="border-radius-bottom" sx={{ backgroundColor: 'steelblue', padding: .6 }} >
                    <h5 style={{ fontWeight: 'bold', fontSize: 'inherit', marginBottom: '0px', marginLeft: '4px', color: 'white' }}>Report Options</h5>
                </Paper>
                <ReportPicker
                    switchReports={switchReports}
                    reportsCategory={reportsCategory}
                    setReportsCategory={setReportsCategory}
                    textFieldStyle={textFieldStyle}
                    reportOptions={reportOptions}
                    setReportName={setReportName}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    reportName={reportName}
                    startDate={startDate}
                    endDate={endDate}
                    disburseDatesList={disburseDatesList}
                    loadingDates={loadingDates}
                    setDisburseDate={setDisburseDate}
                    setPreviousDate={setPreviousDate}
                    setDisburseId={setDisburseId}
                    disburseId={disburseId}
                    mainParameters={mainParameters}
                    setMainParameters={setMainParameters}
                />
            </Paper>

            {/* Report Viewer section */}
            <Paper
                elevation={10}
                sx={{
                    flex: '1', // Remaining 65% width
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                }}
            >
                <TelerikReportViewer
                    ref={el => viewer = el}
                    serviceUrl={`${apiRoute}/api/reports`}
                    reportSource={{ report: `${reportName}.trdp`, parameters: reportParams }}
                    error={(e, args) => {
                        //try {
                        //    console.log('report failed event', e);
                        //    console.log('report failed message', args);

                        //    e?.target?.commands?.stopRendering?.exec();
                        //    e?.target?.reportSource({ report: `LandingReport.trdp`, parameters: {} });
                        //    e?.target?.refreshReport();
                        //} catch (error) {
                        //    console.error("Error inside viewer error handling:", error);
                        //}
                    }}
                    viewerContainerStyle={{
                        position: 'absolute',
                        left: '36.5vw',
                        right: '1.0vw',
                        top: '8.5vh',
                        bottom: '3.4vh',
                        overflow: 'auto',
                        clear: 'both',
                        fontFamily: 'ms sans serif',
                        textTransform: 'none'
                    }}
                    viewMode="INTERACTIVE"
                    scaleMode="SPECIFIC"
                    pageMode="SINGLE_PAGE"
                    scale={1.0}
                    enableAccessibility={false}
                />
            </Paper>
        </Box>
    )
}

export default ReportViewer;